import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();

/**
 * Injects matching voice-search FAQs (from the qa_items knowledge base) into a page.
 * De-dupes against questions already shown on the page, caps the count, and reports the
 * shown items back to the parent via onItems so the parent can add them to its FAQPage schema.
 */
export const MatchingFaq = ({
  serviceLane = null,
  reverseTopic = null,
  franchise = null,
  exclude = [],
  limit = 5,
  onItems,
  title = "More questions families ask",
  eyebrow = "Voice-search answers",
  containerClass = "max-w-3xl",
}) => {
  const [items, setItems] = useState([]);
  const excludeKey = (exclude || []).map(norm).join("|");

  useEffect(() => {
    if (!serviceLane && !reverseTopic) {
      setItems([]);
      if (onItems) onItems([]);
      return;
    }
    const params = { limit: Math.max(limit + 4, 8) };
    if (serviceLane) params.service_lane = serviceLane;
    if (reverseTopic) params.reverse_topic = reverseTopic;
    if (franchise) params.franchise = franchise;
    publicApi
      .get("/public/faq", { params })
      .then(({ data }) => {
        const ex = new Set((exclude || []).map(norm));
        const seen = new Set();
        const list = (data.items || [])
          .filter((it) => {
            const k = norm(it.question);
            if (!k || ex.has(k) || seen.has(k)) return false;
            seen.add(k);
            return true;
          })
          .slice(0, limit);
        setItems(list);
        if (onItems) onItems(list);
      })
      .catch(() => {
        setItems([]);
        if (onItems) onItems([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceLane, reverseTopic, franchise, excludeKey, limit]);

  if (!items.length) return null;

  return (
    <section className="py-12 sm:py-16 bg-[#FBF6EC]" data-testid="matching-faq">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {eyebrow && <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">{eyebrow}</p>}
        <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">{title}</h2>
        <Accordion type="single" collapsible className={`mt-6 ${containerClass}`} data-testid="matching-faq-accordion">
          {items.map((it) => (
            <AccordionItem
              key={it.id}
              value={it.slug || it.id}
              data-testid={`matching-faq-item-${it.slug || it.id}`}
              className="border-b border-[#1C4358]/12"
            >
              <AccordionTrigger className="text-left text-[18px] font-display font-semibold text-[#1C4358] hover:no-underline">
                {it.question}
              </AccordionTrigger>
              <AccordionContent className="text-[16px] text-[#4B5563] leading-relaxed">
                <p>{it.answer}</p>
                {it.slug && (
                  <Link
                    to={`/resources/faq/${it.slug}`}
                    className="mt-3 inline-flex items-center gap-1 text-[14px] font-semibold text-[#9C1E22] hover:underline"
                  >
                    Full answer <ArrowRight size={13} />
                  </Link>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default MatchingFaq;
